import React, { useState, useMemo, ReactNode, useEffect, useRef } from 'react';
import { Box, Stack, useTheme, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'
import { ROUTE_GROUPS } from '../Sidebar/constants'
import { isPathInRouteGroup } from '../use-links'
import { useUser, useHasSitesOnProduction, useModal } from 'store'
import { useSidebar } from './SidebarContext'
import Category, { ItemMenu } from './components/Category';

import {
  MENU_DASHBOARD,
  MENU_SUPERVISION,
  MENU_STATISTICS,
  MENU_DOCUMENTS,
  MENU_WIFI,
  MENU_VOIP,
  MENU_TV_CHROMECAST,
  MENU_EDA,
  MENU_SILENT_MODE,
  MENU_PROJECTS,
  MENU_ADMIN
} from './constants'

interface MenuItem {
  label: string;
  url: string;
  key?: string;
  requiredRoles?: string[];
  adminOnly?: boolean;
}

interface MenuCategory {
  key?: string
  label: string;
  icon: string;
  iconColor?: string;
  url?: string;
  menu?: MenuItem[];
  requiredRoles?: string[];
  disabled?: boolean;
}

interface MenuProps {
  category: MenuCategory;
  minimize?: boolean;
  expandedCategory: boolean;
  onCategoryExpand: (isChevronClick: boolean) => void;
  menuRef?: (el: HTMLDivElement | null) => void;
}

interface CollapseButtonProps {
  expanded: boolean;
  onToggle: () => void;
}

interface SidebarContainerProps {
  expanded: boolean;
  children: ReactNode;
}

const SidebarToggleIcon = ({ variant = 'neutral' }: { variant?: 'neutral' | 'wide' }) => (
  <i className={`fa-kit fa-${variant}-sdb`} style={{ fontSize: 18, fontWeight: 400, lineHeight: 1 }} />
)

const isItemActive = (item: MenuItem, pathname: string): boolean => {
  const routeGroup = Object.values(ROUTE_GROUPS).find(group =>
    item.url.startsWith(group.base)
  )
  return routeGroup
    ? isPathInRouteGroup(pathname, routeGroup)
    : pathname.indexOf(item.url) === 0
}

const Menu: React.FC<MenuProps> = ({ category, minimize = false, expandedCategory, onCategoryExpand, menuRef }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [, setModal] = useModal()
  const [user] = useUser()
  const { t } = useTranslation()

  const authorizedItems = useMemo(() => {
    if (!category.menu?.length) return []
    return category.menu
      .filter((item) => {
        if (!user?.admin && item.adminOnly) {
          return false
        }
        if (!user?.admin && item.requiredRoles) {
          if (!item.requiredRoles.some(role => user?.roles[role])) {
            return false
          }
        }
        return true
      })
  }, [user, category.menu])

  const menu = useMemo(() => {
    const r = authorizedItems
      .map((item, i) => {
        return (
          <ItemMenu
            key={item.url}
            active={isItemActive(item, location.pathname)}
            label={t(item.label)}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (/^http/.test(item.url)) {
                window.open(item.url, 'blank')
              } else {
                navigate(item.url)
              }
            }}
            last={(authorizedItems.length - 1) === i}
          />
        )
      })
    return r.length > 1 ? r : []
  }, [authorizedItems, location, t, navigate])

  const url = useMemo(() => {
    if (category.url) {
      return category.url
    } else if (authorizedItems.length) {
      return authorizedItems[0].url
    }
  }, [category, authorizedItems])

  const active = useMemo(() => {
    if (category.url) {
      return location.pathname.indexOf(category.url) === 0
    } else if (category.menu?.length) {
      return authorizedItems.some(item => isItemActive(item, location.pathname))
    }
  }, [category, authorizedItems, location])

  if (!user?.admin && category.requiredRoles && !category.requiredRoles.some(role => user?.roles[role])) {
    return null
  }
  if (!user?.admin && !category.requiredRoles && category.menu && !authorizedItems.length) {
    return null
  }

  return (
    <Box ref={menuRef} width="100%">
      <Category
        active={active}
        label={t(category.label)}
        icon={category.icon}
        iconColor={category.iconColor}
        minimize={minimize}
        isExpanded={expandedCategory}
        onExpand={onCategoryExpand}
        onClick={() => {
          if (category.disabled) return
          if (url) {
            if (/^http/.test(url)) {
              window.open(url, 'blank')
            } else if (category.key === 'demo') {
              setModal('demo')
            }
            else {
              navigate(url)
            }
          }
        }}
        disabled={category.disabled}
      >
        {menu}
      </Category>
    </Box>
  )
}

const CollapseButton: React.FC<CollapseButtonProps> = ({ expanded, onToggle }) => {
  return (
    <Tooltip
      title={expanded ? 'Réduire la barre latérale' : 'Afficher la barre latérale'}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: '#292929',
            color: 'white',
            borderRadius: '8px',
            fontSize: 10,
            fontWeight: 500,
            px: 1.25,
            py: 0.7,
            minWidth: 132,
            textAlign: 'center',
            boxShadow: '0px 1.85px 3.15px 0px #00000014, 0px 8.15px 6.52px 0px #0000000A, 0px 20px 13px 0px #0000000A'
          }
        },
        arrow: {
          sx: {
            color: '#292929'
          }
        }
      }}
    >
      <Box
        onClick={(event) => {
          event.stopPropagation()
          onToggle()
        }}
        sx={{
          position: 'relative',
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: expanded ? 'transparent' : 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: expanded ? '#CCCCCC' : '#464646',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: '#EFEFEF',
            color: '#464646',
            '& .hover-chevron': {
              opacity: 1
            },
            '& .default-icon': {
              opacity: 0
            },
            '& .collapsed-logo': {
              opacity: 0
            }
          }
        }}>
        {!expanded && (
          <Box
            component="img"
            className="collapsed-logo"
            src="/images/wifirst.svg"
            alt=""
            sx={{ width: 20, height: 21, display: 'block', opacity: 1, position: 'absolute', transition: 'opacity 0.15s ease' }}
          />
        )}
        <Box className="default-icon" sx={{ display: 'flex', opacity: expanded ? 1 : 0, position: 'absolute', transition: 'opacity 0.15s ease' }}>
          <SidebarToggleIcon variant="neutral" />
        </Box>
        <Box
          className="hover-chevron"
          sx={{
            display: 'flex',
            opacity: 0,
            position: 'absolute',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'opacity 0.15s ease'
          }}
        >
          <SidebarToggleIcon variant="wide" />
        </Box>
      </Box>
    </Tooltip>
  )
}

const SidebarContainer: React.FC<SidebarContainerProps> = ({ expanded, children }) => {
  const theme = useTheme()
  return (
    <Stack
      p={0}
      sx={{
        color: theme.palette.grey[700],
        width: !expanded ? '68px' : '272px',
        borderRight: '1px solid #F7F7F7',
        position: 'relative',
        height: '100vh',
        transition: 'width 0.2s ease-in-out, padding 0.2s ease-in-out'
      }}>
      {children}
    </Stack>
  )
}

export const Sidebar: React.FC = () => {
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'))
  const { expanded, setExpanded } = useSidebar()
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const navigate = useNavigate()
  const hasSitesOnProduction = useHasSitesOnProduction()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  useEffect(() => {
    setExpanded(isLargeScreen)
  }, [isLargeScreen])

  const handleCategoryExpand = (categoryLabel: string, isChevronClick: boolean) => {
    const isExpanding = !expandedCategories.includes(categoryLabel)

    if (isChevronClick) {
      setExpandedCategories(prev =>
        prev.includes(categoryLabel)
          ? prev.filter(cat => cat !== categoryLabel)
          : [...prev, categoryLabel]
      )
    } else {
      setExpandedCategories([categoryLabel])
    }

    if (isExpanding && expanded) {
      setTimeout(() => {
        const menuElement = menuRefs.current[categoryLabel]
        if (menuElement && scrollContainerRef.current) {
          const containerRect = scrollContainerRef.current.getBoundingClientRect()
          const elementRect = menuElement.getBoundingClientRect()
          const scrollTop = scrollContainerRef.current.scrollTop
          const offset = elementRect.top - containerRect.top + scrollTop

          scrollContainerRef.current.scrollTo({
            top: offset,
            behavior: 'smooth'
          })
        }
      }, 150)
    }
  }

  return (
    <SidebarContainer expanded={expanded}>
      <Stack justifyContent={'space-between'} height={'100%'}>
        <Stack
          gap={0}
          alignItems={'center'}
          sx={{
            ...(expanded && {
              position: 'relative',
              flexGrow: 1,
              minHeight: 0,
              overflow: 'hidden'
            })
          }}
        >
          <Stack
            direction="row"
            sx={{
              cursor: expanded ? 'pointer' : 'default',
              width: expanded ? '100%' : 68,
              height: 64,
              px: expanded ? 3 : 0,
              alignItems: 'center',
              justifyContent: expanded ? 'space-between' : 'center',
              alignSelf: 'flex-start',
              flexShrink: 0,
              borderBottom: '1px solid #F7F7F7'
            }}
            onClick={() => expanded && navigate('/')}
          >
            {!expanded ? (
              <CollapseButton expanded={expanded} onToggle={() => setExpanded(!expanded)} />
            ) : (
              <>
                <img
                  src={'/images/app_logo.svg'}
                  height={20}
                  width={142}
                  style={{ display: 'block' }}
                />
                <CollapseButton expanded={expanded} onToggle={() => setExpanded(!expanded)} />
              </>
            )}
          </Stack>
          <Stack
            ref={scrollContainerRef}
            alignItems={'center'}
            sx={{
              width: expanded ? '100%' : 68,
              alignSelf: 'flex-start',
              ...(expanded && {
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                paddingBottom: '20px',
                '&::-webkit-scrollbar': {
                  display: 'none'
                }
              })
            }}
          >
            <Stack
              gap={expanded ? 2 : 1}
              alignItems={expanded ? 'start' : 'center'}
              width={expanded ? '100%' : 68}
              px={expanded ? 2 : 0}
              pt={3.5}
            >
              {
                hasSitesOnProduction && (
                  <Menu
                    category={MENU_DASHBOARD}
                    minimize={!expanded}
                    expandedCategory={expandedCategories.includes(MENU_DASHBOARD.label)}
                    onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_DASHBOARD.label, isChevronClick)}
                    menuRef={(el) => menuRefs.current[MENU_DASHBOARD.label] = el}
                  />
                )
              }
              <Menu
                category={MENU_PROJECTS}
                minimize={!expanded}
                expandedCategory={expandedCategories.includes(MENU_PROJECTS.label)}
                onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_PROJECTS.label, isChevronClick)}
                menuRef={(el) => menuRefs.current[MENU_PROJECTS.label] = el}
              />
              <Stack spacing={1} alignItems={expanded ? 'start' : 'center'} width={'100%'}>
                <Menu
                  category={MENU_SUPERVISION}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_SUPERVISION.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_SUPERVISION.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_SUPERVISION.label] = el}
                />
                <Menu
                  category={MENU_STATISTICS}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_STATISTICS.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_STATISTICS.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_STATISTICS.label] = el}
                />
                <Menu
                  category={MENU_DOCUMENTS}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_DOCUMENTS.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_DOCUMENTS.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_DOCUMENTS.label] = el}
                />
              </Stack>
              <Stack spacing={1} alignItems={expanded ? 'start' : 'center'} width={'100%'}>
                <Menu
                  category={MENU_WIFI}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_WIFI.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_WIFI.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_WIFI.label] = el}
                />
                <Menu
                  category={MENU_VOIP}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_VOIP.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_VOIP.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_VOIP.label] = el}
                />
                <Menu
                  category={MENU_TV_CHROMECAST}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_TV_CHROMECAST.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_TV_CHROMECAST.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_TV_CHROMECAST.label] = el}
                />
              </Stack>
              <Stack spacing={1} alignItems={expanded ? 'start' : 'center'} width={'100%'}>
                <Menu
                  category={MENU_EDA}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_EDA.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_EDA.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_EDA.label] = el}
                />
                <Menu
                  category={MENU_SILENT_MODE}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_SILENT_MODE.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_SILENT_MODE.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_SILENT_MODE.label] = el}
                />
                <Menu
                  category={MENU_ADMIN}
                  minimize={!expanded}
                  expandedCategory={expandedCategories.includes(MENU_ADMIN.label)}
                  onCategoryExpand={(isChevronClick) => handleCategoryExpand(MENU_ADMIN.label, isChevronClick)}
                  menuRef={(el) => menuRefs.current[MENU_ADMIN.label] = el}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </SidebarContainer>
  )
}

export default Sidebar
